import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import uniq from 'lodash/uniq';
import includes from 'lodash/includes';
import some from 'lodash/some';
import get from 'lodash/get';
import compact from 'lodash/compact';
import find from 'lodash/find';
import has from 'lodash/has';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import { isPrefix } from '@zedoc/text';
import filter from 'lodash/filter';
import Role from './Role';
import { PERMISSIONS_DOMAIN_DELIMITER } from '../constants';
import BaseModel from './BaseModel';
import PermissionsDomain from './PermissionsDomain';
import { createUtcToLocalTime } from '@zedoc/date';

const constant = (x) => () => x;

const validateDomain = (domain) => {
  if (domain === '') {
    return true;
  }
  if (typeof domain !== 'string') {
    return false;
  }
  return domain.charAt(domain.length - 1) === PERMISSIONS_DOMAIN_DELIMITER;
};

const getGrant = (key, permissions) => {
  if (!has(permissions, key)) {
    return null;
  }
  return {
    tier: permissions[key],
  };
};

/**
 * Represents a User.
 * @class
 */
class User extends BaseModel {
  getEmailAddress() {
    return this.emails && this.emails[0] && this.emails[0].address;
  }

  getPhoneNumber() {
    return this.phones && this.phones[0] && this.phones[0].number;
  }

  getFullName() {
    if (this.name) {
      return this.name;
    }
    if (!this.title && !this.firstName && !this.lastName) return '';
    return compact([this.title, this.firstName, this.lastName]).join(' ');
  }

  getInitials() {
    return this.getFullName()
      .split(' ')
      .map((name) => name[0])
      .join('');
  }

  getPicture() {
    return this.picture;
  }

  /**
   * Return roles that applies exactly to the given domain.
   * @param {String} domain
   * @returns {Role[]}
   */
  getRolesInDomain(domain) {
    return this.roles
      ? this.roles.filter((role) => role.appliesTo === domain)
      : [];
  }

  /**
   * Return roles that applies to domains different than the given one.
   * @param {String} domain
   * @returns {Role[]}
   */
  getRolesNotInDomain(domain) {
    return this.roles
      ? this.roles.filter((role) => role.appliesTo !== domain)
      : [];
  }

  getRolesIdsInDomain(domain) {
    if (!domain) return [];
    return map(this.getRolesInDomain(domain), 'id');
  }

  getRolesIdsNotInDomain(domain) {
    if (!domain) return [];
    return map(this.getRolesNotInDomain(domain), 'id');
  }

  getRolesNamesInDomain(domain) {
    return map(this.getRolesInDomain(domain), 'name');
  }

  getRolesNamesNotInDomain(domain) {
    return map(this.getRolesNotInDomain(domain), 'name');
  }

  getRolesGrantedBy(userId) {
    return filter(this.roles, {
      grantedBy: userId,
    });
  }

  getRolesIdsGrantedBy(userId) {
    return map(this.getRolesGrantedBy(userId), 'id');
  }

  getRolesNamesGrantedBy(userId) {
    return map(this.getRolesGrantedBy(userId), 'name');
  }

  isMemberOf(domainOrDomains, groupType) {
    return !!find(this.groups, (group) => {
      if (groupType && group.type !== groupType) {
        return false;
      }
      if (isArray(domainOrDomains)) {
        return includes(domainOrDomains, group.appliesTo);
      }
      if (typeof domainOrDomains === 'string') {
        return group.appliesTo === domainOrDomains;
      }
      return false;
    });
  }

  getLockedInTimeWindow() {
    return this.lockedInTimeWindow;
  }

  isLockedInTimeWindow() {
    if (this.lockedInTimeWindow?.locked && this.lockedInTimeWindow?.timezone) {
      const { dateStart, dateEnd, timeStart, timeEnd, timezone } =
        this.lockedInTimeWindow;

      // Case 1: No start or end date, but times and timezone are set (for every day)
      if (
        isEmpty(dateStart) &&
        isEmpty(dateEnd) &&
        !isEmpty(timeStart) &&
        !isEmpty(timeEnd) &&
        !isEmpty(timezone)
      ) {
        const dateStart = moment.tz(timezone).format('YYYY-MM-DD');
        const dateEnd = dateStart;

        const startDateTime = moment.tz(`${dateStart} ${timeStart}`, timezone);
        const endDateTime = moment.tz(`${dateEnd} ${timeEnd}`, timezone);
        const currentDateTime = moment.tz(timezone);
        const isCurrentInRange = currentDateTime.isBetween(
          startDateTime,
          endDateTime,
        );
        if (isCurrentInRange) {
          console.log('Day based Account is locked');
          return true; // Account is locked
        } else {
          console.log('Day based  Account is not locked');
          return false; // Account is not locked
        }
      }

      // Case 2: Start date, end date, times, and timezone are all set (specific date range)
      else if (!isEmpty(dateStart) && !isEmpty(dateEnd) && !isEmpty(timezone)) {
        const timeStartToUse = !isEmpty(timeStart) ? timeStart : '00:00:00';
        const timeEndToUse = !isEmpty(timeEnd) ? timeEnd : '23:59:59';

        const startDateTime = moment.tz(
          `${dateStart} ${timeStartToUse}`,
          timezone,
        );
        const endDateTime = moment.tz(`${dateEnd} ${timeEndToUse}`, timezone);
        const currentDateTime = moment.tz(timezone);
        const isCurrentInRange = currentDateTime.isBetween(
          startDateTime,
          endDateTime,
        );
        if (isCurrentInRange) {
          return true; // Account is locked
        } else {
          return false; // Account is not locked
        }
      }
      // Invalid configuration or no valid locking rule
      else {
        return false; // Account is not locked
      }
    }
    return false; // Account is not locked if 'locked' is false
  }

  /**
   * Return a list of all roles that appliesTo to "relativeTo" domain.
   * If relativeTo is provided, it must be a full domain path
   * (so in particular it must end with PATH_DELIMITER)
   * or and array of domain paths. If "relativeTo"
   * is not specified return all roles.
   */
  getRoles({ relativeTo = '' } = {}) {
    if (
      relativeTo !== '' &&
      typeof relativeTo === 'string' &&
      !validateDomain(relativeTo)
    ) {
      return [];
    }
    let allowedDomains;
    if (isArray(relativeTo)) {
      allowedDomains = filter(relativeTo, validateDomain);
    }
    const roles = filter(this.roles, (role) => {
      if (!role || !role.appliesTo) {
        return false;
      }
      const predicate = isPrefix(role.appliesTo, PERMISSIONS_DOMAIN_DELIMITER);
      if (allowedDomains) {
        return some(allowedDomains, predicate);
      }
      if (relativeTo) {
        return predicate(relativeTo);
      }
      return true;
    });
    return roles;
  }

  /**
   * Return a list of all roles that appliesTo to domain
   * or it's sub-domain; this is useful for filtering roles
   * that are relevant to a specific organization.
   */
  getRolesWithinDomain(domain) {
    if (!validateDomain(domain)) {
      return [];
    }
    if (domain === '') {
      return this.roles || [];
    }
    const predicate = isPrefix(domain, PERMISSIONS_DOMAIN_DELIMITER);
    const roles = filter(this.roles, (role) => {
      if (!role || !role.appliesTo) {
        return false;
      }
      return role.appliesTo === domain || predicate(role.appliesTo);
    });
    return roles;
  }

  getRolesIds(options) {
    return map(this.getRoles(options), 'id');
  }

  getRolesNames(options) {
    return map(this.getRoles(options), 'name');
  }

  hasRole(roleId) {
    return !!find(this.roles, ({ id }) => id === roleId);
  }

  getRolesTags(options) {
    const selected = {};
    this.getRoles(options).forEach(({ tags }) =>
      tags.forEach(({ name }) => {
        selected[name] = true;
      }),
    );
    return Object.keys(selected);
  }

  hasRoleTag(tag, options) {
    const roles = this.getRoles(options);
    for (let i = 0; i < roles.length; i += 1) {
      const { tagsNames } = roles[i];
      if (tagsNames && tagsNames.some((name) => name === tag)) {
        return true;
      }
    }
    return false;
  }

  getGroups({ inAnyOf, relativeTo, type } = {}) {
    let domains;
    let predicate = constant(true);
    if (isArray(inAnyOf)) {
      domains = filter(inAnyOf, validateDomain);
      predicate = domains
        ? (() => {
            const predicates = map(domains, (domain) =>
              isPrefix(domain, PERMISSIONS_DOMAIN_DELIMITER),
            );
            return (domain) => some(predicates, (p) => p(domain));
          })()
        : constant(true);
    } else if (relativeTo) {
      const finalRelativeTo = isArray(relativeTo) ? relativeTo : [relativeTo];
      domains = filter(finalRelativeTo, validateDomain);
      predicate = domains
        ? (() => {
            return (domain) =>
              some(domains, isPrefix(domain, PERMISSIONS_DOMAIN_DELIMITER));
          })()
        : constant(true);
    }

    if (type) {
      return filter(
        this.groups,
        (group) => group.type === type && predicate(group.appliesTo),
      );
    }

    return filter(this.groups, (group) => predicate(group.appliesTo));
  }

  getGroupsIds(options) {
    return map(this.getGroups(options), 'id');
  }

  getGroupsInDomain(domain) {
    return filter(this.groups, (group) => group.appliesTo === domain);
  }

  getGroupsIdsInDomain(domain) {
    return map(this.getGroupsInDomain(domain), 'id');
  }

  getGroupsNamesInDomain(domain) {
    return map(this.getGroupsInDomain(domain), 'name');
  }

  getGroupsNames(options) {
    return map(this.getGroups(options), 'name');
  }

  formatRecentActivity() {
    if (!this.recentAction) return '';
    return moment(this.recentAction.time).calendar(null, {
      sameElse: 'DD/MM/YYYY',
    });
  }

  getFailedLoginAttempts() {
    return this.failedLoginAttempts || 0;
  }

  isLocked() {
    return !!this.locked;
  }

  isPasswordLoginDisabled() {
    return !!this.disablePasswordLogin;
  }

  isAzure() {
    return (
      !isEmpty(this.integrationId) &&
      get(this, 'integrationSource') === 'OAuth/azure'
    );
  }

  isRemoved() {
    return isEmpty(this.groups);
  }

  /**
   * Evaluates sum of all permissions relative to the given domain.
   * If "relativeTo" is not provided, then permissions from all roles are returned.
   * @param {Object} options
   * @param {String} options.relativeTo
   * @param {Object} options.rolesDB
   * @param {Object} options.groupsDB
   * @param {String} options.scope
   * @returns {Object}
   */
  getAllPermissions({ relativeTo = '', rolesDB, groupsDB, scope } = {}) {
    return Role.extractFlatPermissions({
      rolesIds: this.getRolesIds({
        relativeTo,
      }),
      rolesDB,
      groupsIds: this.getGroupsIds({
        relativeTo,
      }),
      groupsDB,
      scope,
    });
  }

  getPermissionsValidator(options) {
    const permissions = this.getAllPermissions(options);
    const validateOne = (requested) => {
      if (requested && typeof requested === 'string') {
        return getGrant(requested, permissions);
      }
      if (
        requested &&
        typeof requested === 'object' &&
        typeof requested.key === 'string'
      ) {
        const granted = getGrant(requested.key, permissions);
        if (has(requested, 'tier')) {
          if (typeof requested.tier !== 'number') {
            // eslint-disable-next-line react/no-this-in-sfc
            this.constructor.logger
              .warn(`Permission tier was specified for ${requested.key}
  but invalid value was provided: ${requested.tier}. Expected number.`);
            return null;
          }
          if (!granted || granted.tier < requested.tier) {
            return null;
          }
        }
        return granted;
      }
      return null;
    };
    return (condition) => {
      if (isArray(condition)) {
        let tier = Infinity;
        // The resulting tier will be the minimum of all granted tiers.
        for (let i = 0; i < condition.length; i += 1) {
          const c = condition[i];
          const granted = validateOne(c);
          if (!granted) {
            return null;
          }
          tier = Math.min(granted.tier, tier);
        }
        return {
          tier,
        };
      }
      return validateOne(condition);
    };
  }

  /**
   * Return all domains (realm) at where the user has all the given primitive permission.
   * @param {string} permission
   */
  getDomainsWithPrimitivePermission(
    permission,
    { tier, simplify = true, rolesDB = {}, groupsDB = {} } = {},
  ) {
    const domains = [];
    const roles = this.roles || [];
    const allRoles = [
      ...roles,
      ...flatMap(this.groups, ({ id }) => {
        const group = groupsDB[id] || { roles: [] };
        return map(group.roles, (role) => ({
          id: role.id,
          appliesTo: group.belongsTo,
        }));
      }),
    ];

    if (!isEmpty(allRoles)) {
      allRoles.forEach((role) => {
        if (
          Role.roleHasPermission({
            permission,
            tier,
            roleId: role.id,
            rolesDB,
          })
        ) {
          domains.push(role.appliesTo);
        }
      });
    }

    if (simplify) {
      return PermissionsDomain.extractFundamentalDomains(domains);
    }

    return uniq(domains);
  }

  /**
   * Return all domain grants for the given primitive permission, each carrying
   * the source role's onlyAssigned flag. Grants on the same domain are NOT
   * deduplicated here — selector composition handles "unrestricted wins" per
   * Option A semantics.
   * @param {string} permission
   */
  getDomainGrantsWithPrimitivePermission(
    permission,
    { tier, rolesDB = {}, groupsDB = {} } = {},
  ) {
    const grants = [];
    const roles = this.roles || [];
    const allRoles = [
      ...roles,
      ...flatMap(this.groups, ({ id }) => {
        const group = groupsDB[id] || { roles: [] };
        return map(group.roles, (role) => ({
          id: role.id,
          appliesTo: group.belongsTo,
        }));
      }),
    ];

    if (!isEmpty(allRoles)) {
      allRoles.forEach((role) => {
        if (
          Role.roleHasPermission({
            permission,
            tier,
            roleId: role.id,
            rolesDB,
          })
        ) {
          const roleDoc = rolesDB[role.id];
          grants.push({
            domain: role.appliesTo,
            onlyAssigned:
              !!role.onlyAssigned || !!(roleDoc && roleDoc.onlyAssigned),
          });
        }
      });
    }

    return grants;
  }

  /**
   * Return all domains (realm) at where the user has all the given permissions.
   * @param {string|string[]} permissions - can be a string (for one permission) or an array of strings
   */
  getPermissionsRealm(permissions, options) {
    const { scope, rolesDB, groupsDB } = options || {};
    const realms = [];
    if (scope) {
      realms.push(scope);
    }
    if (!isArray(permissions)) {
      return this.getPermissionsRealm([permissions], options);
    }
    permissions.forEach((permission) => {
      let permissionKey;
      let tier;
      if (typeof permission === 'string') {
        permissionKey = permission;
      } else if (
        typeof permission === 'object' &&
        typeof permission.key === 'string'
      ) {
        permissionKey = permission.key;
        if (typeof permission.tier === 'number') {
          ({ tier } = permission);
        }
      } else {
        permissionKey = null;
      }
      if (permissionKey) {
        realms.push(
          this.getDomainsWithPrimitivePermission(permissionKey, {
            tier,
            simplify: false,
            rolesDB,
            groupsDB,
          }),
        );
      }
    });
    return PermissionsDomain.findCommonRealm(realms);
  }

  getPermissionsRealmSelectors(field, permissions, options) {
    return PermissionsDomain.selectors(
      this.getPermissionsRealm(permissions, options),
      field,
    );
  }

  getTopLevelDomains() {
    return PermissionsDomain.extractFundamentalDomains(
      map(this.groups, 'appliesTo'),
    );
  }

  getTopLevelDomainsByRoles() {
    return PermissionsDomain.extractFundamentalDomains(
      map(this.roles, 'appliesTo'),
    );
  }

  hasPermission(key, options) {
    const permissions = this.getAllPermissions(options);
    if (isArray(key)) {
      return key.every((k) => !!getGrant(k, permissions));
    }
    return !!getGrant(key, permissions);
  }

  getReference() {
    return {
      id: this._id,
      name: this.getFullName(),
      email: this.getEmailAddress(),
      phone: this.getPhoneNumber(),
    };
  }

  static selectIfHasRoleInDomain(domain) {
    return {
      'roles.appliesTo': domain,
    };
  }
}

User.collection = 'users';
User.piiFields = ['name', 'emails', 'phones', 'picture'];

export default User;
